import React, { ReactElement, useState } from 'react';

import { WidgetType } from 'domain/entity/widget';
import { useFormChangesView } from 'adapter/in/common/context/formChanges';
import {
    dispatchCurrentWidgetConfiguration,
    KeyAndNamePair,
    useCurrentWidgetConfiguration,
} from 'adapter/in/common/context/currentWidgetConfiguration';
import WidgetActionsMenu from 'adapter/in/dashboards/pages/Widget/ActionsMenu';
import DeleteWidgetModalContainer from 'adapter/in/dashboards/containers/Modals/DeleteWidgetModal';
import { dispatchWidgetMenuView, useWidgetMenuView } from 'adapter/in/dashboards/context/EditDashboard/WidgetMenuView';
import { dispatchDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import { useWidgetActionsDiscardChangesCleanupActions } from 'adapter/in/dashboards/containers/Modals/DiscardChangesModal/useDiscardChangesCleanupActions';
import { ConfigFieldType } from 'adapter/in/common/form/types';

type WidgetActionsMenuProps = {
    widgetType: WidgetType;
    dashboardId: string;
    widgetId: string;
    newPreset: KeyAndNamePair;
    newFeature: KeyAndNamePair;
    showMenu: boolean;
    showOptions: boolean;
};

export const WidgetActionsMenuContainer = (props: WidgetActionsMenuProps): ReactElement => {
    const { dashboardId, widgetId, widgetType, newFeature, newPreset, showMenu, showOptions } = props;
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { tab } = useWidgetMenuView();
    const { hasChange } = useFormChangesView();
    const { editWidget } = dispatchWidgetMenuView();
    const { setCurrentWidgetConfiguration } = dispatchCurrentWidgetConfiguration();
    const { setModalDiscardChanges } = dispatchDashboardEdit();
    const { widgetId: activeWidgetId } = useCurrentWidgetConfiguration();
    const { switchEditWidgetCleanupAction } = useWidgetActionsDiscardChangesCleanupActions();

    const isEditDisabled = (tabPane: ConfigFieldType) => widgetId === activeWidgetId && tab === tabPane;

    const withClickedWidget = (tabPane: ConfigFieldType) => {
        switchEditWidgetCleanupAction({
            id: widgetId,
            type: widgetType,
            feature: newFeature,
            preset: newPreset,
            tabPane,
        });
    };

    return (
        <>
            <DeleteWidgetModalContainer
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                dashboardId={dashboardId}
                widgetId={widgetId}
            />
            <WidgetActionsMenu
                show={showMenu}
                showOptions={showOptions}
                widgetType={widgetType}
                onDelete={() => {
                    setShowDeleteModal(true);
                }}
                isEditDisabled={(tabPane: ConfigFieldType) => isEditDisabled(tabPane)}
                onEdit={(tabPane: ConfigFieldType) => {
                    if (hasChange) {
                        setModalDiscardChanges(true, () => withClickedWidget(tabPane));
                    } else {
                        setCurrentWidgetConfiguration(widgetType, newFeature, newPreset, widgetId);
                        editWidget(tabPane);
                    }
                }}
            />
        </>
    );
};

export default WidgetActionsMenuContainer;
